import { PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";

import WorkspaceEnvVarSelect from "./WorkspaceEnvVarSelect";

import { Mcp } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DiscoveredMcpTool,
  McpServer,
  McpServerConfig,
  McpServerInput,
  useCreateMcpServer,
  useTestMcpServer,
  useUpdateMcpServer,
} from "@/queries/mcp-servers";
import { useListEnvVars } from "@/queries/env-vars";
import { displayErrorToast } from "@/utils/toast";

interface McpServerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server: McpServer | null;
  workspaceId: number;
  userToken: string;
}

interface HeaderRow {
  id: string;
  name: string;
  envVar: string;
}

const emptyConfig: McpServerConfig = {
  type: "mcp",
  server_url: "",
  headers: {},
};

const McpServerDialog = ({
  open,
  onOpenChange,
  server,
  workspaceId,
  userToken,
}: McpServerDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [serverUrl, setServerUrl] = useState("");
  const [serverLabel, setServerLabel] = useState("");
  const [allowedTools, setAllowedTools] = useState("");
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [discoveredTools, setDiscoveredTools] = useState<DiscoveredMcpTool[]>([]);
  const createServer = useCreateMcpServer(workspaceId, userToken);
  const updateServer = useUpdateMcpServer(workspaceId, userToken);
  const testServer = useTestMcpServer(workspaceId, userToken);
  const envVars = useListEnvVars(
    { scope: "workspace", workspaceId },
    { enabled: open && workspaceId > 0 },
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const config = server?.config ?? emptyConfig;
    setName(server?.name ?? "");
    setDescription(server?.description ?? "");
    setServerUrl(config.server_url);
    setServerLabel(config.server_label ?? "");
    setAllowedTools(config.allowed_tools?.join(", ") ?? "");
    setHeaders(
      Object.entries(config.headers ?? {}).map(([headerName, reference]) => ({
        id: crypto.randomUUID(),
        name: headerName,
        envVar: reference.env_var,
      })),
    );
    setDiscoveredTools([]);
  }, [open, server]);

  const parseAllowedTools = (value: string) =>
    value
      .split(",")
      .map(tool => tool.trim())
      .filter(Boolean);

  const buildConfig = (): McpServerConfig => {
    const selectedTools = parseAllowedTools(allowedTools);
    return {
      type: "mcp",
      server_url: serverUrl.trim(),
      headers: Object.fromEntries(
        headers
          .filter(header => header.name.trim() && header.envVar.trim())
          .map(header => [header.name.trim(), { env_var: header.envVar.trim() }]),
      ),
      ...(serverLabel.trim() ? { server_label: serverLabel.trim() } : {}),
      ...(selectedTools.length ? { allowed_tools: selectedTools } : {}),
    };
  };

  const handleTest = async () => {
    try {
      setDiscoveredTools(await testServer.mutateAsync(buildConfig()));
    } catch (error) {
      displayErrorToast(error);
    }
  };

  const handleSave = async () => {
    const input: McpServerInput = {
      name: name.trim(),
      description: description.trim() || null,
      config: buildConfig(),
    };
    try {
      if (server) {
        await updateServer.mutateAsync({ id: server.id, input });
      } else {
        await createServer.mutateAsync(input);
      }
      onOpenChange(false);
    } catch (error) {
      displayErrorToast(error);
    }
  };

  const toggleAllowedTool = (toolName: string) => {
    setAllowedTools(current => {
      const selectedTools = parseAllowedTools(current);
      if (selectedTools.includes(toolName)) {
        return selectedTools.filter(selectedName => selectedName !== toolName).join(", ");
      }
      return [...selectedTools, toolName].join(", ");
    });
  };

  const isPending = createServer.isPending || updateServer.isPending;
  const selectedToolNames = parseAllowedTools(allowedTools);
  const selectedToolSet = new Set(selectedToolNames);
  const hasInvalidHeaders = headers.some(
    header =>
      !header.name.trim() ||
      !header.envVar ||
      !envVars.data?.some(envVar => envVar.key === header.envVar && !envVar.is_empty),
  );
  const canSave = Boolean(name.trim() && serverUrl.trim()) && !hasInvalidHeaders && !isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mcp className="h-5 w-5" />
            {server ? "Edit MCP Server" : "New MCP Server"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="mcp-name">Name</Label>
            <Input
              id="mcp-name"
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder="GitHub MCP"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="mcp-description">Description</Label>
            <Textarea
              id="mcp-description"
              value={description}
              onChange={event => setDescription(event.target.value)}
              placeholder="Tools for searching repositories and issues"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="mcp-url">Server URL</Label>
            <Input
              id="mcp-url"
              value={serverUrl}
              onChange={event => setServerUrl(event.target.value)}
              placeholder="https://mcp.example.com/mcp"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="mcp-label">Server Label (optional)</Label>
            <Input
              id="mcp-label"
              value={serverLabel}
              onChange={event => setServerLabel(event.target.value)}
              placeholder="github"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Label>Headers</Label>
                <p className="text-xs text-muted-foreground">
                  Map HTTP header names to encrypted workspace environment variable names.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setHeaders(current => [
                    ...current,
                    { id: crypto.randomUUID(), name: "", envVar: "" },
                  ])
                }
              >
                <PlusIcon className="mr-1 h-4 w-4" />
                Add Header
              </Button>
            </div>
            {headers.map((header, index) => (
              <div className="flex gap-2" key={header.id}>
                <Input
                  value={header.name}
                  onChange={event =>
                    setHeaders(current =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, name: event.target.value } : item,
                      ),
                    )
                  }
                  placeholder="Authorization"
                />
                <WorkspaceEnvVarSelect
                  value={header.envVar}
                  envVars={envVars.data ?? []}
                  workspaceId={workspaceId}
                  isLoading={envVars.isLoading}
                  onChange={value =>
                    setHeaders(current =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, envVar: value } : item,
                      ),
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    setHeaders(current => current.filter((_, itemIndex) => itemIndex !== index))
                  }
                >
                  <Trash2Icon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="mcp-tools">Allowed Tools (optional)</Label>
            <Input
              id="mcp-tools"
              value={allowedTools}
              onChange={event => setAllowedTools(event.target.value)}
              placeholder="search, fetch"
            />
            <p className="text-xs text-muted-foreground">Comma-separated remote tool names.</p>
          </div>

          {discoveredTools.length > 0 && (
            <div className="rounded-md border p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-sm font-medium">Available Tools ({discoveredTools.length})</p>
                {selectedToolNames.length > 0 && (
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setAllowedTools("")}
                  >
                    Clear Selection
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {discoveredTools.map(tool => (
                  <button
                    type="button"
                    className={`rounded border px-2 py-1 text-xs ${
                      selectedToolSet.has(tool.name)
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-transparent bg-muted hover:border-gray-300"
                    }`}
                    onClick={() => toggleAllowedTool(tool.name)}
                    key={tool.name}
                  >
                    {tool.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleTest()}
            disabled={!serverUrl.trim() || hasInvalidHeaders || testServer.isPending}
          >
            {testServer.isPending ? "Testing..." : "Test Connection"}
          </Button>
          <Button onClick={() => void handleSave()} disabled={!canSave}>
            {isPending ? "Saving..." : "Save MCP Server"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default McpServerDialog;
